import { Link } from "react-router-dom";
import { BsWhatsapp } from "react-icons/bs";
import { MdCheckCircle } from "react-icons/md";

const planTypes = [
  {
    icon: "🏠",
    title: "Bungalow Plans",
    desc: "Single-storey designs from 2–6+ bedrooms. Practical, elegant, and cost-effective.",
    link: "/bungalows",
    tag: "Most Popular",
  },
  {
    icon: "🏢",
    title: "Duplex Plans",
    desc: "Two-storey designs with flexible layouts for growing families or rental income.",
    link: "/duplex",
    tag: null,
  },
  {
    icon: "🏬",
    title: "Flat / Apartment Plans",
    desc: "Multi-unit residential designs ideal for urban living and investment properties.",
    link: "/flat",
    tag: null,
  },
  {
    icon: "🏗️",
    title: "Non-Residential Plans",
    desc: "Commercial, industrial, and institutional designs built to specification.",
    link: "/non-residential",
    tag: null,
  },
];

const included = [
  "Architectural drawings (all elevations)",
  "Floor plan with room dimensions",
  "Site plan & foundation layout",
  "Professional stamp for regulatory approval",
  "Structural details",
  "WhatsApp support throughout",
];

function BuyPlans() {
  return (
    <div className="font-Montserrat min-h-screen bg-gray-50">
      {/* Spacer for fixed navbar */}
      <div className="h-[56px] md:h-[72px]" />

      {/* Hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-12 md:py-20 text-center">
          <p className="text-[#1877F2] uppercase tracking-[0.3em] text-xs md:text-sm font-semibold mb-3">
            Get Started
          </p>
          <h1 className="text-2xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Professional Building Plans<br className="hidden md:block" /> at Affordable Prices
          </h1>
          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
            Every plan includes full architectural drawings, a professional stamp
            for regulatory submission, and dedicated WhatsApp support — ready to
            build from day one.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/featured-house-plans"
              className="bg-[#1877F2] hover:bg-[#0e5fc7] text-white font-bold py-3 px-9 rounded-full transition-colors duration-200 text-sm md:text-base"
            >
              Browse All Plans
            </Link>
            <a
              href="https://wa.me/+2349033020343"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white font-bold py-3 px-9 rounded-full transition-all duration-200 text-sm md:text-base"
            >
              <BsWhatsapp />
              Request Custom Plan
            </a>
          </div>
        </div>
      </div>

      {/* Plan categories */}
      <div className="max-w-6xl mx-auto px-4 md:px-10 py-14 md:py-20">
        <div className="text-center mb-12">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">Browse by Type</h2>
          <p className="text-gray-500 text-sm">Choose the plan category that fits your project.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {planTypes.map(({ icon, title, desc, link, tag }) => (
            <Link
              key={title}
              to={link}
              className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 p-6 flex gap-5 items-start"
            >
              <div className="w-12 h-12 bg-[#FFF8EC] rounded-xl flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-[#1877F2] transition-colors duration-300">
                {icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-900 group-hover:text-[#1877F2] transition-colors">{title}</h3>
                  {tag && (
                    <span className="text-[10px] font-bold text-white bg-[#1877F2] px-2 py-0.5 rounded-full">
                      {tag}
                    </span>
                  )}
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                <span className="text-[#1877F2] text-xs font-semibold mt-2 inline-block group-hover:underline">
                  View Plans →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* What's included */}
      <div className="bg-white border-y border-gray-100 py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-10">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">What's Included in Every Plan</h2>
            <p className="text-gray-500 text-sm">No surprises. Everything you need to break ground.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {included.map((item) => (
              <div key={item} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <MdCheckCircle className="text-[#1877F2] text-xl flex-shrink-0" />
                <p className="text-gray-700 text-sm font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom plan CTA */}
      <div className="bg-[#1877F2] py-14 md:py-20 text-center text-white">
        <h2 className="text-xl md:text-3xl font-bold mb-3">Need a Custom Design?</h2>
        <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
          Don't see exactly what you're looking for? Our team can design a plan
          tailored to your land size, lifestyle, and budget.
        </p>
        <a
          href="https://wa.me/+2349033020343?text=Hello, I'd like to request a custom building plan."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-white text-[#1877F2] hover:bg-gray-100 font-bold py-3.5 px-9 rounded-full transition-colors duration-200 text-sm md:text-base"
        >
          <BsWhatsapp className="text-green-500" />
          Request a Custom Plan
        </a>
      </div>
    </div>
  );
}

export default BuyPlans;
